import type { TrailStep } from '@/components/system';
import type { BehaviorObservationSurvey } from '@/types/report';

/**
 * O vocabulário do questionário de observação: as perguntas, as respostas e os
 * passos que elas produzem.
 *
 * Os passos não são uma lista fixa — saem das próprias respostas, então
 * `buildSurveySteps` é chamado a cada render e a trilha cresce ou encolhe junto.
 */

/** Um grupo de opções marcáveis dentro de um passo. */
export type QuestionGroup<TField extends string> = {
    id: TField;
    title: string;
    options: string[];
};

export type BasicField = keyof NonNullable<BehaviorObservationSurvey['basic']>;
export type ChildField = keyof NonNullable<BehaviorObservationSurvey['child_behavior']>;
export type MentalField = keyof NonNullable<BehaviorObservationSurvey['mental_state']>;
export type Under12Type = NonNullable<BehaviorObservationSurvey['under_12_types']>[number];

export const BASIC_QUESTIONS: QuestionGroup<BasicField>[] = [
    {
        id: 'posture',
        title: '1. Postura geral durante as sessões',
        options: [
            'Colaborativo(a)',
            'Parcialmente colaborativo(a)',
            'Resistente',
            'Necessitou de incentivo frequente',
        ],
    },
    {
        id: 'attention',
        title: '2. Atenção e engajamento',
        options: [
            'Atenção sustentada adequada',
            'Flutuações atencionais observadas',
            'Distração frequente',
            'Necessitou de redirecionamentos',
        ],
    },
    {
        id: 'pace',
        title: '3. Ritmo e organização da execução',
        options: [
            'Ritmo compatível com a tarefa',
            'Ritmo lento',
            'Ritmo acelerado',
            'Dificuldade de organização e planejamento',
        ],
    },
    {
        id: 'instructions',
        title: '4. Compreensão e seguimento de instruções',
        options: [
            'Compreensão imediata',
            'Necessitou de repetições de instruções',
            'Necessitou de esclarecimentos adicionais',
            'Seguiu instruções adequadamente',
        ],
    },
    {
        id: 'frustration',
        title: '5. Reação a erros, frustração e feedback',
        options: [
            'Aceitação de erros adequada',
            'Sinais de frustração',
            'Desistência diante da dificuldade',
            'Reações emocionais observáveis',
        ],
    },
    {
        id: 'verbal_interaction',
        title: '6. Comunicação verbal e interação',
        options: [
            'Interação adequada com o avaliador',
            'Pouca iniciativa espontânea na interação',
            'Comunicação verbal adequada e coerente com o contexto',
            'Comunicação verbal reduzida/retraída',
            'Comunicação prolixa, com dificuldade de manter fluxo coerente',
        ],
    },
    {
        id: 'non_verbal',
        title: '7. Comunicação não verbal',
        options: [
            'Comunicação não verbal adequada',
            'Expressividade facial reduzida',
            'Alterações no tom de voz/prosódia',
            'Linguagem corporal incongruente',
            'Contato visual reduzido',
        ],
    },
    {
        id: 'relevant_behaviors',
        title: '8. Comportamentos relevantes observados (recomendado)',
        options: [
            'Inquietação motora',
            'Lentificação psicomotora',
            'Impulsividade comportamental',
            'Evitação de tarefas',
            'Perseveração comportamental',
            'Comportamentos repetitivos/estereotipados',
            'Incômodo com estímulos sensoriais (auditivos, táteis, visuais)',
        ],
    },
];

export const CHILD_BEHAVIOR_GROUPS: QuestionGroup<ChildField>[] = [
    {
        id: 'language',
        title: 'Linguagem funcional',
        options: [
            'Vocabulário compatível com a idade',
            'Dificuldades na organização da fala ou construção frasal',
            'Dificuldades no uso pragmático da linguagem (ex.: contexto, metáforas, turnos)',
        ],
    },
    {
        id: 'rules',
        title: 'Seguimento de regras, instruções e organização comportamental',
        options: [
            'Seguiu instruções e regras de forma adequada',
            'Necessitou de repetição ou mediação para seguir instruções',
            'Resistência ao cumprimento de regras ou limites',
            'Apresentou comportamentos disruptivos',
            'Interrupções frequentes das atividades',
            'Oposição verbal ou comportamental',
            'Reações emocionais proporcionais diante de erros, limites ou perdas',
            'Irritação ou frustração intensa diante de erros, limites ou perdas',
        ],
    },
    {
        id: 'play_social',
        title: 'Brincar e reciprocidade social',
        options: [
            'Brincar e interação compatíveis com a idade',
            'Dificuldade em sustentar brincadeiras compartilhadas',
            'Uso incomum ou rígido de brinquedos',
        ],
    },
];

export const MENTAL_STATE_GROUPS: QuestionGroup<MentalField>[] = [
    {
        id: 'consciousness',
        title: '1. Nível de consciência e responsividade',
        options: [
            'Alerta',
            'Sonolento(a)',
            'Oscilações de alerta',
            'Responde adequadamente a estímulos',
        ],
    },
    {
        id: 'orientation',
        title: '2. Orientação',
        options: [
            'Orientado(a) no tempo',
            'Orientado(a) no espaço',
            'Orientado(a) quanto à própria pessoa',
            'Desorientação parcial',
            'Desorientação global',
        ],
    },
    {
        id: 'language',
        title: '3. Linguagem e comunicação',
        options: [
            'Linguagem compreensiva preservada',
            'Linguagem expressiva adequada',
            'Lentificação da fala',
            'Dificuldades de nomeação',
            'Empobrecimento do vocabulário',
        ],
    },
    {
        id: 'memory',
        title: '4. Memória (observação clínica)',
        options: [
            'Recordação adequada de informações recentes',
            'Dificuldade para evocar informações recentes',
            'Repetição de perguntas',
            'Memória remota preservada',
            'Dificuldade em evocar fatos antigos',
        ],
    },
    {
        id: 'thought',
        title: '5. Pensamento (forma e curso)',
        options: [
            'Pensamento organizado',
            'Lentificação do pensamento',
            'Perseveração',
            'Dificuldade de abstração observável',
        ],
    },
    {
        id: 'affect',
        title: '6. Afetividade e comportamento emocional',
        options: ['Afeto congruente', 'Afeto restrito', 'Labilidade emocional', 'Apatia observada'],
    },
    {
        id: 'motor',
        title: '7. Motricidade',
        options: [
            'Motricidade preservada',
            'Lentificação motora',
            'Inquietação motora',
            'Dificuldades em motricidade fina observáveis',
            'Dificuldades em motricidade grossa observáveis',
        ],
    },
    {
        id: 'insight',
        title: '8. Insight e julgamento',
        options: [
            'Reconhece suas dificuldades',
            'Reconhecimento parcial',
            'Baixa consciência das próprias dificuldades',
        ],
    },
];

export const UNDER_12_OPTIONS: Array<{ id: Under12Type; title: string }> = [
    { id: 'mental_state', title: 'Estado mental (descrição ampliada)' },
    { id: 'child_behavior', title: 'Comportamento infantil (observação detalhada)' },
    { id: 'basic_behavior', title: 'Observação comportamental básica' },
];

export type SurveyStepId = 'under12-types' | 'over12-detail' | 'basic' | 'child' | 'mental';

export type SurveyStep = {
    id: SurveyStepId;
    /** Título do diálogo enquanto o passo está aberto. */
    title: string;
    /** Versão curta, para o chip da trilha. */
    trailLabel: string;
    description?: string;
    /**
     * Pergunta do passo, quando as opções respondem a uma. Fica acima delas, no
     * corpo do diálogo: uma pergunta pede resposta imediata, e a descrição do
     * cabeçalho — acima da trilha — a afasta demais das opções.
     */
    question?: string;
};

const STEP_DEFS: Record<SurveyStepId, SurveyStep> = {
    'under12-types': {
        id: 'under12-types',
        title: 'Tipos de descrição desejados',
        trailLabel: 'Tipos de descrição',
        description: 'Escolha uma ou mais descrições para compor a observação.',
    },
    'over12-detail': {
        id: 'over12-detail',
        title: 'Nível de detalhamento',
        trailLabel: 'Detalhamento',
        question: 'Este caso pede uma observação detalhada do estado mental?',
    },
    basic: {
        id: 'basic',
        title: 'Observação comportamental básica',
        trailLabel: 'Observação básica',
    },
    child: {
        id: 'child',
        title: 'Comportamento infantil',
        trailLabel: 'Comportamento infantil',
    },
    mental: {
        id: 'mental',
        title: 'Exame do estado mental',
        trailLabel: 'Estado mental',
    },
};

export const toggleArrayValue = <T extends string>(values: T[] = [], value: T) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

export const getAgeGroupFromPatient = (patientAge?: number) => {
    if (patientAge == null) return undefined;
    return patientAge < 12 ? 'under_12' : 'over_12';
};

export const hasAnySelections = (survey: BehaviorObservationSurvey) => {
    const hasBasic = Object.values(survey.basic || {}).some(
        (value) => Array.isArray(value) && value.length > 0,
    );
    const hasChild = Object.values(survey.child_behavior || {}).some(
        (value) => Array.isArray(value) && value.length > 0,
    );
    const hasMental = Object.values(survey.mental_state || {}).some(
        (value) => Array.isArray(value) && value.length > 0,
    );
    const hasNotes = !!survey.general_notes?.trim();

    return hasBasic || hasChild || hasMental || hasNotes;
};

/**
 * Se o rascunho já carrega alguma resposta.
 *
 * Mais largo que `hasAnySelections`: aqui até a faixa etária conta, porque a
 * pergunta é "esse rascunho foi tocado?" e não "dá para gerar um texto com ele?".
 */
export const hasSurveyData = (survey?: BehaviorObservationSurvey) => {
    if (!survey) return false;

    const hasOver12Detail =
        survey.over_12_needs_mental_state !== null &&
        survey.over_12_needs_mental_state !== undefined;

    return (
        !!survey.age_group ||
        (survey.under_12_types || []).length > 0 ||
        hasOver12Detail ||
        hasAnySelections(survey)
    );
};

/**
 * Os passos do questionário, na ordem, dadas as respostas até agora.
 *
 * A primeira pergunta de cada faixa etária decide quais passos vêm depois, então
 * a lista muda enquanto a pessoa responde. Quem chama precisa lidar com um
 * índice que ficou fora do fim.
 */
export const buildSurveySteps = (survey: BehaviorObservationSurvey): SurveyStep[] => {
    if (survey.age_group === 'under_12') {
        const types = survey.under_12_types || [];

        return [
            STEP_DEFS['under12-types'],
            ...(types.includes('basic_behavior') ? [STEP_DEFS.basic] : []),
            ...(types.includes('child_behavior') ? [STEP_DEFS.child] : []),
            ...(types.includes('mental_state') ? [STEP_DEFS.mental] : []),
        ];
    }

    return [
        STEP_DEFS['over12-detail'],
        ...(survey.over_12_needs_mental_state === true ? [STEP_DEFS.mental] : []),
        ...(survey.over_12_needs_mental_state === false ? [STEP_DEFS.basic] : []),
    ];
};

export const toTrailSteps = (steps: SurveyStep[]): TrailStep[] =>
    steps.map((step) => ({ key: step.id, label: step.trailLabel }));
